import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/atoms";
import { useGetOnboardingStatusQuery, useUpdateSectionStatusMutation } from "@/redux/apis/onboarding/onboardingApi";
import { AddOnsSection } from "@/redux/slices/onboarding/types";
import { SetupPanel, SetupPanelItem, WhyDoINeedModal, WalkthroughModal, ReadSetupGuideModal } from "../common";
import { quickSetupContent, quickSetupExplanations } from "../data/quickSetupContent";
import { isSectionComplete, isPostSimulationSectionsUnlocked } from "../helpers/onboardingHelpers";
import { useQuickSetupNavigation } from "../hooks";

const AddonsPaths = {
  ADD_ONS: "/add-ons",
} as const;

enum ModalType {
  ADD_ONS_WHY = "add-ons-why-do-i-need",
  ADD_ONS_WALKTHROUGH = "add-ons-walkthrough",
  ADD_ONS_DOCUMENTATION = "add-ons-documentation",
}

export const AddonsPanel = () => {
  const navigate = useNavigate();
  const { setQuickSetupFlag } = useQuickSetupNavigation();
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);

  const { data: onboardingStatus } = useGetOnboardingStatusQuery();
  const [updateSectionStatus] = useUpdateSectionStatusMutation();

  const isAddOnsCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.addOns.setupAddons) : false;

  const completedSteps = isAddOnsCompleted ? 1 : 0;
  const totalSteps = 1;

  const isUnlocked = isPostSimulationSectionsUnlocked(onboardingStatus);

  const handleModalClose = () => setActiveModal(null);

  const handleAddOnsAction = () => {
    setQuickSetupFlag(AddOnsSection.SETUP_ADDONS);
    navigate(AddonsPaths.ADD_ONS);

    if (onboardingStatus && !isAddOnsCompleted) {
      updateSectionStatus({ section: AddOnsSection.SETUP_ADDONS, completed: true }).catch((error) => {
        console.error("Failed to update section status:", error);
      });
    }
  };

  const handleAddOnsWhyDoThis = () => {
    setActiveModal(ModalType.ADD_ONS_WHY);
  };

  const handleAddOnsWatchVideo = () => {
    setActiveModal(ModalType.ADD_ONS_WALKTHROUGH);
  };

  const handleAddOnsReadDocumentation = () => {
    setActiveModal(ModalType.ADD_ONS_DOCUMENTATION);
  };

  const addOnsFeaturedIcon = {
    name: "Bag2",
    background: "bg-primary-light",
  };

  return (
    <SetupPanel
      stepNumber={6}
      title="Add Ons"
      description="Enable extra features to enhance your RideMinder experience"
      stepCount={totalSteps}
      completedSteps={completedSteps}
      isLocked={!isUnlocked}
    >
      <SetupPanelItem
        icon={addOnsFeaturedIcon.name}
        iconBackground={addOnsFeaturedIcon.background}
        title="Setup Add Ons"
        isCompleted={isAddOnsCompleted}
        actions={[
          {
            label: "Go to Add Ons",
            onClick: handleAddOnsAction,
            variant: "primary",
          },
        ]}
        helpOptions={[
          {
            label: "Why do I need to do this?",
            icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
            onClick: handleAddOnsWhyDoThis,
          },
          { label: "Watch walkthrough video", icon: "Video", onClick: handleAddOnsWatchVideo },
          { label: "Read Setup Guide", icon: "Document", onClick: handleAddOnsReadDocumentation },
        ]}
      />

      <WhyDoINeedModal
        open={activeModal === ModalType.ADD_ONS_WHY}
        onClose={handleModalClose}
        title="Why do I need to setup add-ons?"
        featuredIcon={addOnsFeaturedIcon}
        explanations={quickSetupExplanations.addons.setup}
      />

      <WalkthroughModal
        featuredIcon={addOnsFeaturedIcon}
        open={activeModal === ModalType.ADD_ONS_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Add-ons)"
        videoUrl={quickSetupContent.addons.setup.videoUrl}
      />

      <ReadSetupGuideModal
        featuredIcon={addOnsFeaturedIcon}
        open={activeModal === ModalType.ADD_ONS_DOCUMENTATION}
        onClose={handleModalClose}
        title="Add-Ons Setup Guide"
        documentationUrl={quickSetupContent.addons.setup.documentationUrl}
        pdfUrl={quickSetupContent.addons.setup.pdfUrl}
      />
    </SetupPanel>
  );
};
